import { oidcLogoutUrl, oidcPostLogoutRedirectUri } from '../firebaseConfig';

/**
 * Utah ID (id.utah.gov / ForgeRock AM) OIDC RP-initiated logout helper.
 *
 * To end the id.utah.gov SSO session the browser is sent to AM's
 * `end_session_endpoint` (`<issuer>/connect/endSession`, from the IdP's OIDC
 * discovery document — NOT the bare `mylogin.utah.gov/logout?goto=` host, which
 * is not a functional logout endpoint). That endpoint REQUIRES an
 * `id_token_hint` (the id.utah.gov-issued id_token — AM 400s without it) and
 * only redirects back to a `post_logout_redirect_uri` that is EXACTLY registered
 * on the AM OAuth2 client.
 *
 * Firebase exchanges the IdP id_token for its own token and discards the
 * original, and sign-out usually happens after a reload (in-memory copy gone),
 * so the id.utah.gov id_token is captured at sign-in
 * (`firebaseAuth.completeRedirect` → `setIdpIdToken`) and mirrored to
 * localStorage so it survives reloads and is available as the `id_token_hint` at
 * logout. This is a single-purpose logout credential — NOT session/role state
 * (Rule 12) — the same auth-flow plumbing pattern as the auto-signin guard.
 */

// Storage key for the id.utah.gov id_token used as the logout `id_token_hint`.
const STORAGE_KEY = 'utah_sso_id_token';

const safeStore = (get) => {
  try {
    return get();
  } catch {
    return null;
  }
};
const localStore = () =>
  safeStore(() => (typeof localStorage !== 'undefined' ? localStorage : null));
const sessionStore = () =>
  safeStore(() =>
    typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  );

// In-memory copy so a logout in the same page session works without storage.
let idpIdToken = null;

/**
 * Capture the id.utah.gov id_token (the logout `id_token_hint`) at sign-in.
 * Mirrored to localStorage so it survives the reload before a later sign-out.
 */
export const setIdpIdToken = (idToken) => {
  idpIdToken = idToken || null;
  try {
    if (idToken) {
      localStore()?.setItem(STORAGE_KEY, idToken);
    } else {
      localStore()?.removeItem(STORAGE_KEY);
    }
  } catch {
    /* storage unavailable — the in-memory copy still serves same-session logout */
  }
};

/** The captured id.utah.gov id_token (memory first, then persisted copy), or null. */
export const getIdpIdToken = () => {
  if (idpIdToken) {
    return idpIdToken;
  }
  try {
    return localStore()?.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

/**
 * Purge the captured id.utah.gov id_token from memory and browser storage
 * (localStorage + any legacy sessionStorage copy). Called on sign-out once the
 * hint has been consumed into the logout URL.
 */
export const clearIdpIdToken = () => {
  idpIdToken = null;
  try {
    localStore()?.removeItem(STORAGE_KEY);
    sessionStore()?.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
};

/**
 * Best-effort delete of the app origin's first-party cookies (expire each on the
 * root path) as sign-out hygiene. Cookies scoped to another domain/path or set
 * HttpOnly are not JS-readable and cannot be removed here; this clears the
 * JS-visible ones before the logout redirect is issued.
 */
export const clearAppCookies = () => {
  try {
    if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
      return;
    }
    document.cookie
      .split(';')
      .map((entry) => entry.split('=')[0].trim())
      .filter(Boolean)
      .forEach((name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });
  } catch {
    /* cookies unavailable — nothing to clear */
  }
};

/**
 * True when a REGISTERED `post_logout_redirect_uri` is configured, i.e. AM can
 * redirect the browser back to the app after logout. When false, sign-out must
 * not do a full-page IdP redirect (it would strand the user on AM's logged-out
 * page) — the app signs out locally and lands on its own signed-out page.
 */
export const canReturnToApp = () => Boolean(oidcPostLogoutRedirectUri);

/**
 * Build AM's `endSession` logout URL with the required `id_token_hint` and,
 * when configured (registered), the `post_logout_redirect_uri` that returns the
 * browser to the app. Returns null when no endpoint or no `id_token_hint` is
 * available (AM 400s without the hint, so there is nothing useful to redirect to).
 */
export const buildOidcLogoutUrl = () => {
  const hint = getIdpIdToken();
  if (!oidcLogoutUrl || !hint) {
    return null;
  }
  try {
    const url = new URL(oidcLogoutUrl);
    url.searchParams.set('id_token_hint', hint);
    if (oidcPostLogoutRedirectUri) {
      url.searchParams.set('post_logout_redirect_uri', oidcPostLogoutRedirectUri);
    }
    return url.toString();
  } catch {
    return null;
  }
};
